import React, { useEffect, useState } from "react";
import { Badge, Button, Card, CardHeader, Field, Input, MessageBar, MessageBarBody, Spinner, Text, Title1, makeStyles, tokens } from "@fluentui/react-components";
import { useApiClient } from "../hooks/useApiClient";
const useStyles = makeStyles({
 container: { display: "flex", flexDirection: "column", gap: "24px", padding: "24px", maxWidth: "1400px", margin: "0 auto" },
 settingsCard: { display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: tokens.spacingHorizontalM }
});
export default function AdminSettingsPage() {
 const styles = useStyles();
 const { getAdminSettings, updateAdminSettings } = useApiClient();
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
  // Global interest rate settings state
  const [globalInterestRate, setGlobalInterestRate] = useState<number>(0.15);
  const [interestRateInput, setInterestRateInput] = useState<string>("15.0");
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSaved, setSettingsSaved] = useState(false);

  // Approver email state (scoped to the admin's entity)
  const [entityId, setEntityId] = useState("");
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);

  // Email domain -> entity mapping (global admins only)
  const [domainRows, setDomainRows] = useState<[string, string][]>([]);
  const [newDomain, setNewDomain] = useState("");
  const [newDomainEntity, setNewDomainEntity] = useState("");
  const [savingDomains, setSavingDomains] = useState(false);
  const [domainsError, setDomainsError] = useState<string | null>(null);
  const [domainsSaved, setDomainsSaved] = useState(false);
  const [level1Emails, setLevel1Emails] = useState<string[]>([]);
  const [level2Emails, setLevel2Emails] = useState<string[]>([]);
  const [newLevel1Input, setNewLevel1Input] = useState("");
  const [newLevel2Input, setNewLevel2Input] = useState("");
  const [savingApprovers, setSavingApprovers] = useState(false);
  const [approversError, setApproversError] = useState<string | null>(null);
  const [approversSaved, setApproversSaved] = useState(false);


 useEffect(() => {
  let active = true;
  getAdminSettings().then(settings => {
   if (!active) return;
   setGlobalInterestRate(settings.interestRate);
   setInterestRateInput((settings.interestRate * 100).toFixed(1));
   setEntityId(settings.entityId ?? "");
   setIsGlobalAdmin(!!settings.isGlobalAdmin);
   setDomainRows(Object.entries(settings.entityDomains ?? {}).sort());
   setLevel1Emails(settings.level1ApproverEmails ?? []);
   setLevel2Emails(settings.level2ApproverEmails ?? []);
  }).catch(err => { if (active) setError(err.message || "Failed to load settings"); })
    .finally(() => { if (active) setLoading(false); });
  return () => { active = false; };
 }, [getAdminSettings]);
  const handleSaveSettings = async () => {
    const rate = parseFloat(interestRateInput);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      setSettingsError("Interest rate must be a number between 0 and 100.");
      return;
    }
    setSavingSettings(true);
    setSettingsError(null);
    setSettingsSaved(false);
    try {
      const saved = await updateAdminSettings({ interestRate: rate / 100 });
      setGlobalInterestRate(saved.interestRate);
      setInterestRateInput((saved.interestRate * 100).toFixed(1));
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (err: any) {
      setSettingsError(err.message || "Failed to save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAddDomain = () => {
    const domain = newDomain.trim().toLowerCase().replace(/^@/, "");
    const entity = newDomainEntity.trim().toLowerCase();
    if (!domain || !entity) return;
    setDomainRows([...domainRows.filter(([d]) => d !== domain), [domain, entity] as [string, string]].sort());
    setNewDomain("");
    setNewDomainEntity("");
  };

  const handleSaveDomains = async () => {
    setSavingDomains(true);
    setDomainsError(null);
    setDomainsSaved(false);
    try {
      const saved = await updateAdminSettings({ entityDomains: Object.fromEntries(domainRows) });
      setDomainRows(Object.entries(saved.entityDomains ?? {}).sort());
      setDomainsSaved(true);
      setTimeout(() => setDomainsSaved(false), 3000);
    } catch (err: any) {
      setDomainsError(err.message || "Failed to save domain mapping.");
    } finally {
      setSavingDomains(false);
    }
  };

  const handleAddLevel1Email = () => {
    const email = newLevel1Input.trim();
    if (!email || !email.includes("@")) return;
    if (!level1Emails.includes(email)) {
      setLevel1Emails([...level1Emails, email]);
    }
    setNewLevel1Input("");
  };

  const handleAddLevel2Email = () => {
    const email = newLevel2Input.trim();
    if (!email || !email.includes("@")) return;
    if (!level2Emails.includes(email)) {
      setLevel2Emails([...level2Emails, email]);
    }
    setNewLevel2Input("");
  };

  const handleSaveApprovers = async () => {
    setSavingApprovers(true);
    setApproversError(null);
    setApproversSaved(false);
    try {
      const saved = await updateAdminSettings({
        level1ApproverEmails: level1Emails,
        level2ApproverEmails: level2Emails,
      });
      setLevel1Emails(saved.level1ApproverEmails ?? []);
      setLevel2Emails(saved.level2ApproverEmails ?? []);
      setApproversSaved(true);
      setTimeout(() => setApproversSaved(false), 3000);
    } catch (err: any) {
      setApproversError(err.message || "Failed to save approvers.");
    } finally {
      setSavingApprovers(false);
    }
  };


 if (loading) return <Spinner label="Loading settings..." />;
 if (error) return <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>;
 return <div className={styles.container}><Title1>Admin Settings</Title1>
      {/* Global Settings */}
      <Card>
        <CardHeader
          header={
            <Text weight="semibold" size={500}>
              Global Settings
            </Text>
          }
          description={
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              Shared by all entities.{!isGlobalAdmin && " Only global administrators can change these."}
            </Text>
          }
        />
        <div className={styles.settingsCard}>
          <Field
            label={`Global Interest Rate (WACC) — currently ${(globalInterestRate * 100).toFixed(2)}%`}
            validationState={settingsError ? "error" : "none"}
            validationMessage={settingsError ?? undefined}
          >
            <Input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={interestRateInput}
              onChange={(_, data) => {
                setInterestRateInput(data.value);
                setSettingsError(null);
                setSettingsSaved(false);
              }}
              contentAfter={<Text>%</Text>}
              style={{ width: "140px" }}
              disabled={!isGlobalAdmin}
            />
          </Field>
          <Button
            appearance="primary"
            onClick={handleSaveSettings}
            disabled={savingSettings || !isGlobalAdmin}
          >
            {savingSettings ? "Saving…" : "Save"}
          </Button>
          {settingsSaved && (
            <Text style={{ color: tokens.colorPaletteGreenForeground1, alignSelf: "center" }}>
              Saved ✓
            </Text>
          )}
        </div>
      </Card>

      {/* Email domain -> entity mapping (global admins only) */}
      {isGlobalAdmin && (
        <Card>
          <CardHeader
            header={<Text weight="semibold" size={500}>Entity Mapping by Email Domain</Text>}
            description={
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Users are assigned to the entity of their email domain. Users from unmapped domains
                cannot use the system unless their user record has an explicit entity or they are
                global administrators.
              </Text>
            }
          />
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "0 4px 4px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", minHeight: "30px" }}>
              {domainRows.length === 0 && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, alignSelf: "center" }}>
                  No domains mapped.
                </Text>
              )}
              {domainRows.map(([domain, entity]) => (
                <Badge
                  key={domain}
                  appearance="outline"
                  shape="rounded"
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => setDomainRows(domainRows.filter(([d]) => d !== domain))}
                  title="Click to remove"
                >
                  @{domain} → {entity} ×
                </Badge>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "flex-end" }}>
              <Field label="Email domain">
                <Input
                  placeholder="example.com"
                  value={newDomain}
                  onChange={(_: unknown, data: { value: string }) => setNewDomain(data.value)}
                  style={{ minWidth: "200px" }}
                />
              </Field>
              <Field label="Entity ID">
                <Input
                  placeholder="ocuk"
                  value={newDomainEntity}
                  onChange={(_: unknown, data: { value: string }) => setNewDomainEntity(data.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && handleAddDomain()}
                  style={{ minWidth: "140px" }}
                />
              </Field>
              <Button onClick={handleAddDomain} disabled={!newDomain.trim() || !newDomainEntity.trim()}>
                Add
              </Button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Button appearance="primary" onClick={handleSaveDomains} disabled={savingDomains}>
                {savingDomains ? "Saving…" : "Save Mapping"}
              </Button>
              {domainsSaved && <Text style={{ color: tokens.colorPaletteGreenForeground1 }}>Saved ✓</Text>}
              {domainsError && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{domainsError}</Text>}
            </div>
          </div>
        </Card>
      )}

      {/* Approvers */}
      <Card>
        <CardHeader
          header={
            <Text weight="semibold" size={500}>
              Approval Workflow — Approvers{entityId && ` for ${entityId}`}
            </Text>
          }
          description={
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              These people can approve your entity's purchase requests and receive the
              notification emails at each approval stage. Other entities manage their own
              approvers{isGlobalAdmin && " (switch entity in the header)"}. Requests can only be
              submitted once both levels have at least one approver. Changes take effect immediately.
            </Text>
          }
        />
        <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "0 4px 4px" }}>
          {/* Level 1 approvers */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Text weight="semibold">Level 1 Approvers</Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              Notified when a new purchase request is submitted.
            </Text>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", minHeight: "30px" }}>
              {level1Emails.length === 0 && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, alignSelf: "center" }}>
                  No level-1 approvers configured.
                </Text>
              )}
              {level1Emails.map((email) => (
                <Badge
                  key={email}
                  appearance="outline"
                  shape="rounded"
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => setLevel1Emails(level1Emails.filter((e) => e !== email))}
                  title="Click to remove"
                >
                  {email} ×
                </Badge>
              ))}
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
              <Field label="Add email">
                <Input
                  type="email"
                  placeholder="approver@example.com"
                  value={newLevel1Input}
                  onChange={(_: unknown, data: { value: string }) => setNewLevel1Input(data.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && handleAddLevel1Email()}
                  style={{ minWidth: "260px" }}
                />
              </Field>
              <Button onClick={handleAddLevel1Email} disabled={!newLevel1Input.includes("@")}>
                Add
              </Button>
            </div>
          </div>

          {/* Level 2 approvers */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Text weight="semibold">Level 2 Approvers</Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              Notified after a level-1 approval, and when requests expire.
            </Text>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", minHeight: "30px" }}>
              {level2Emails.length === 0 && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, alignSelf: "center" }}>
                  No level-2 approvers configured.
                </Text>
              )}
              {level2Emails.map((email) => (
                <Badge
                  key={email}
                  appearance="outline"
                  shape="rounded"
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => setLevel2Emails(level2Emails.filter((e) => e !== email))}
                  title="Click to remove"
                >
                  {email} ×
                </Badge>
              ))}
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
              <Field label="Add email">
                <Input
                  type="email"
                  placeholder="approver@example.com"
                  value={newLevel2Input}
                  onChange={(_: unknown, data: { value: string }) => setNewLevel2Input(data.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && handleAddLevel2Email()}
                  style={{ minWidth: "260px" }}
                />
              </Field>
              <Button onClick={handleAddLevel2Email} disabled={!newLevel2Input.includes("@")}>
                Add
              </Button>
            </div>
          </div>

          {/* Save approvers */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Button
              appearance="primary"
              onClick={handleSaveApprovers}
              disabled={savingApprovers}
            >
              {savingApprovers ? "Saving…" : "Save Approvers"}
            </Button>
            {approversSaved && (
              <Text style={{ color: tokens.colorPaletteGreenForeground1 }}>Saved ✓</Text>
            )}
            {approversError && (
              <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{approversError}</Text>
            )}
          </div>
        </div>
      </Card>


 </div>;
}
